<script setup lang="ts">
/*
 * Token-styled modal built on Headless UI's Dialog. Keyboard + focus
 * trapping handled by Headless. The brand variant is borderless (the brand
 * header + popup shadow delineate it) with a slightly tighter `rounded-2xl`
 * radius; the default variant keeps the full card radius + app border so the
 * six existing non-brand modals look as they did before the brand restyle.
 *
 * `headerVariant='brand'` paints the header with the brand violet gradient
 * (white title/description/close-icon) per the Job Grade modals; the default
 * keeps the original light header. The panel is a flex column capped at 90vh
 * with a fixed (non-scrolling) header + footer and a scrollable body, so tall
 * forms scroll internally instead of overflowing the viewport.
 */
import { computed } from 'vue';
import {
  Dialog,
  DialogPanel,
  DialogTitle,
  DialogDescription,
  TransitionChild,
  TransitionRoot,
} from '@headlessui/vue';
import { X } from 'lucide-vue-next';

const props = withDefaults(
  defineProps<{
    open: boolean;
    title?: string;
    description?: string;
    size?: 'sm' | 'md' | 'lg' | 'xl';
    /** Hide the default close × button when you want the user to commit/cancel only. */
    hideClose?: boolean;
    /** 'brand' uses the brand violet gradient header with white text. */
    headerVariant?: 'default' | 'brand';
    /**
     * Allow an outside (overlay) click or ESC to dismiss the modal. Defaults to
     * `false`: a modal closes ONLY via the × button or a footer Close, so an
     * accidental click outside never discards in-progress input.
     */
    dismissible?: boolean;
  }>(),
  { size: 'md', headerVariant: 'default', dismissible: false },
);

const emit = defineEmits<{ close: [] }>();

// Headless UI fires @close for BOTH ESC and backdrop-click — swallow it unless
// `dismissible`, so only the × / footer Close can dismiss.
function onDialogClose() {
  if (props.dismissible) emit('close');
}

const sizeClass = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-2xl',
  xl: 'max-w-4xl',
}[props.size];

const isBrand = computed(() => props.headerVariant === 'brand');
</script>

<template>
  <TransitionRoot :show="open" as="template">
    <Dialog as="div" class="relative z-50" @close="onDialogClose">
      <TransitionChild
        as="template"
        enter="duration-150 ease-out"
        enter-from="opacity-0"
        enter-to="opacity-100"
        leave="duration-100 ease-in"
        leave-from="opacity-100"
        leave-to="opacity-0"
      >
        <div class="fixed inset-0 bg-app-fg/40" aria-hidden="true" />
      </TransitionChild>

      <div class="fixed inset-0 flex items-center justify-center p-4">
        <TransitionChild
          as="template"
          enter="duration-200 ease-out"
          enter-from="opacity-0 scale-95"
          enter-to="opacity-100 scale-100"
          leave="duration-150 ease-in"
          leave-from="opacity-100 scale-100"
          leave-to="opacity-0 scale-95"
        >
          <DialogPanel
            :class="[
              'flex max-h-[90vh] w-full flex-col overflow-hidden bg-app-surface shadow-popup',
              isBrand ? 'rounded-2xl' : 'rounded-card border border-app-border',
              sizeClass,
            ]"
          >
            <header
              v-if="title || !hideClose"
              :class="[
                'flex shrink-0 items-start justify-between gap-4 px-6 py-4',
                isBrand
                  ? 'bg-gradient-to-r from-primary-600 to-primary text-white'
                  : 'border-b border-app-border',
              ]"
            >
              <div class="flex flex-col gap-0.5">
                <DialogTitle
                  v-if="title"
                  :class="['text-lg font-semibold', isBrand ? 'text-white' : 'text-app-fg']"
                >
                  {{ title }}
                </DialogTitle>
                <DialogDescription
                  v-if="description"
                  :class="['text-md', isBrand ? 'text-white/80' : 'text-app-muted']"
                >
                  {{ description }}
                </DialogDescription>
              </div>
              <button
                v-if="!hideClose"
                type="button"
                :class="[
                  'inline-flex cursor-pointer items-center justify-center rounded-lg p-1 transition-colors',
                  isBrand
                    ? 'text-white/90 ring-1 ring-inset ring-white/40 hover:bg-white/15 hover:text-white hover:ring-white/60'
                    : 'text-app-muted ring-1 ring-inset ring-app-border hover:bg-slate-100 hover:text-app-fg',
                ]"
                aria-label="Close"
                @click="$emit('close')"
              >
                <X class="h-4 w-4" aria-hidden="true" />
              </button>
            </header>
            <div class="flex-1 overflow-y-auto px-6 py-5"><slot /></div>
            <footer
              v-if="$slots.footer"
              class="flex shrink-0 items-center justify-end gap-2 border-t border-app-border bg-slate-50/50 px-6 py-3"
            >
              <slot name="footer" />
            </footer>
          </DialogPanel>
        </TransitionChild>
      </div>
    </Dialog>
  </TransitionRoot>
</template>
